// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useContext, useLayoutEffect, useRef } from 'react';
import { execute } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { CopilotContext, createLocalAnswerKey } from '../../CopilotContext';
import {
    ConsumeStream,
    GraphQlErrorLike,
    StreamChunk,
    StreamPayload,
    applyStreamChunk,
    isSettleChunk,
    isTransportError,
    userErrorMessage,
} from './chatStream';

const STREAM_OBJECT_DELAY_MS = 10;

type Options = {
    // Called after each batch of chunks is applied, for the chat to scroll and focus
    onChunksApplied: (focusInput: boolean) => void;
};

/**
 * The chat's stream consumer, shared by questions and tool-call runs.
 */
const useChatStream = ({ onChunksApplied }: Options): ConsumeStream => {
    const { setAnswers, chatId, setChatId } = useContext(CopilotContext);
    const client = useApolloClient();

    const chatIdRef = useRef(chatId);
    useLayoutEffect(() => {
        chatIdRef.current = chatId;
    });

    /**
     * Executes a streamed mutation - an answer or a run - applying every chunk to the current
     * answers as it arrives, never to a copy taken when the stream started: a history
     * reload in between must not be overwritten. useMutation() resolves only once the whole
     * response has arrived, so the link chain is executed directly.
     */
    const consumeStream: ConsumeStream = async (query, variables, rootField, { onInitial, focusInput, chatGuard }) => {
        const stale = () => chatGuard !== undefined && chatIdRef.current !== chatGuard;
        const stream = {
            queue: [] as StreamChunk[],
            done: false,
            failed: false,
            // Whether it failed because the response did not get through
            lostInTransport: false,
            refusal: null as string | null,
            initialError: null as string | null,
            wake: () => {},
        };

        execute(client.link, { query, variables, context: { multipartStream: true } }, { client }).subscribe({
            next: result => {
                if ('data' in result) {
                    const initial = (result.data as Record<string, StreamPayload> | null | undefined)?.[rootField];
                    if (initial) {
                        stream.initialError = initial.runError ?? initial.error ?? null;
                        if (!stale()) {
                            onInitial?.(initial);
                        }
                        initial.contentChunks?.forEach(chunk => chunk && stream.queue.push(chunk));
                    }
                }
                if ('incremental' in result) {
                    result.incremental?.forEach(entry => {
                        if ('items' in entry) {
                            (entry.items as unknown as (StreamChunk | null)[]).forEach(chunk => chunk && stream.queue.push(chunk));
                        }
                        if (entry.errors?.length) {
                            // A chunk that could not be delivered arrives as a null item
                            stream.failed = true;
                        }
                    });
                }
                if ('errors' in result && result.errors?.length) {
                    stream.refusal ??= userErrorMessage(result.errors as GraphQlErrorLike[]);
                    stream.failed = true;
                }
                stream.wake();
            },
            error: error => {
                stream.failed = true;
                stream.lostInTransport = isTransportError(error);
                stream.done = true;
                stream.wake();
            },
            complete: () => {
                stream.done = true;
                stream.wake();
            },
        });

        let progressKey: string | null = null;
        let received = 0;
        // Whether the server ended the stream with an error chunk
        let errorChunk = false;

        while (!stream.done || stream.queue.length > 0) {
            if (stream.queue.length === 0) {
                await new Promise<void>(resolve => {
                    stream.wake = resolve;
                });
                stream.wake = () => {};
                continue;
            }

            const typing = !document.hidden;
            // When the tab is visible chunks are applied one by one for smooth text
            // streaming; in a hidden tab they are applied in a single batch
            const chunks = typing ? [stream.queue.shift() as StreamChunk] : stream.queue.splice(0);
            received += chunks.length;
            if (stale()) {
                continue;
            }

            // The turn in progress gets its key outside the state updater, at its first chunk
            const keyed: { chunk: StreamChunk; key: string }[] = [];
            for (const chunk of chunks) {
                if (!isSettleChunk(chunk)) {
                    progressKey ??= createLocalAnswerKey();
                }
                if (chunk.error) {
                    errorChunk = true;
                }
                keyed.push({ chunk, key: progressKey ?? '' });
                if (chunk.chatId && chunk.chatId !== chatId) {
                    setChatId(chunk.chatId);
                }
            }
            setAnswers(prev => keyed.reduce((acc, { chunk, key }) => applyStreamChunk(acc, chunk, key, chatId), prev));
            onChunksApplied(focusInput);
            if (typing) {
                // A short delay for smoother text streaming in the UI
                await new Promise(resolve => setTimeout(resolve, STREAM_OBJECT_DELAY_MS));
            }
        }

        const finalKey = progressKey;
        if (finalKey !== null && !stale()) {
            setAnswers(prev => prev.map(answer => (answer.key === finalKey && answer.streaming ? { ...answer, streaming: false } : answer)));
        }

        return {
            received,
            failed: stream.failed,
            lostInTransport: stream.lostInTransport,
            errorChunk,
            refusal: stream.refusal,
            initialError: stream.initialError,
            progressKey: finalKey,
        };
    };

    return consumeStream;
};

export default useChatStream;
